import { DatePicker, Form, Input, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { logActivity, type ActivityType } from './api';

const { Text } = Typography;

interface ActivityFormValues {
  outcome?: string;
  note?: string;
  nextActionAt?: Dayjs;
}

/**
 * Opened by the one-tap buttons on the lead page, with the activity type already
 * chosen. The advisor's remaining job is one line about what happened and when
 * to try again — anything more and it gets skipped between customers.
 */
export function LogActivityDrawer({
  leadUid,
  type,
  open,
  onClose,
  onLogged,
}: {
  leadUid: string;
  type: ActivityType;
  open: boolean;
  onClose: () => void;
  onLogged: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<ActivityFormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const values = await form.validateFields();
    setSubmitting(true);
    setFailure(null);
    try {
      await logActivity(leadUid, {
        type,
        ...(values.outcome !== undefined ? { outcome: values.outcome } : {}),
        ...(values.note !== undefined ? { note: values.note } : {}),
        ...(values.nextActionAt !== undefined ? { nextActionAt: values.nextActionAt.toISOString() } : {}),
      });
      form.resetFields();
      onLogged();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : 'Could not save the activity.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionDrawer
      open={open}
      title={t(`crm.activity.${type}`)}
      submitLabel={t('crm.saveActivity')}
      submitting={submitting}
      onSubmit={() => void submit()}
      onClose={() => {
        setFailure(null);
        onClose();
      }}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ nextActionAt: dayjs().add(1, 'day') }}
      >
        <Form.Item name="outcome" label={t('crm.fields.outcome')}>
          <Input size="large" placeholder={t('crm.outcomePlaceholder')} autoFocus />
        </Form.Item>
        <Form.Item name="note" label={t('fields.notes')}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="nextActionAt" label={t('crm.fields.nextAction')} extra={t('crm.nextActionHelp')}>
          <DatePicker showTime size="large" style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}
